import { useState } from "react";
import { isValidNationalId, normaliseNationalId, type CurrentUser } from "@talent/protocol";
import { AuthError, updateMe } from "../auth-api.js";
import { COPY, fill } from "../copy.js";
import { digitsOnly, fa } from "../format.js";
import { useClasses } from "./class-context.js";
import { ScreenHeader } from "./screen-header.js";

/**
 * The teacher's own page: who the system thinks they are, and the way out.
 *
 * **Signing out lives here rather than at the foot of the roster.** It was the last thing on the
 * longest screen, which put an account action directly under the list a teacher scrolls every day and
 * gave the roster a footer that had nothing to do with the class.
 *
 * **Two fields are editable and two are not, and the split is not arbitrary.** A name and a national
 * id were typed once into an application and both get typed wrongly, so a teacher can fix their own.
 * The phone number cannot be changed here because it IS the credential — moving an account to a new
 * number with nothing proving that number's holder agreed, and no password to fall back on, is the one
 * edit this product must not offer. The school cannot be changed because approval was granted on it;
 * changing it unilaterally would undo somebody's decision. Both are shown, with the reason beside them.
 */

const CARD = "rounded-lg bg-card shadow-card";
const FIELD =
  "h-[52px] w-full rounded-md border-2 border-edge bg-card px-md text-body outline-none transition-colors focus-visible:border-grape";
const PRIMARY =
  "min-h-touch w-full rounded-pill bg-grape px-lg py-md text-heading text-card shadow-tint-grape disabled:opacity-60";
const LABEL = "mb-1 block text-caption text-muted";

export function ProfileScreen({
  user,
  onBack,
  onSignOut,
}: {
  user: CurrentUser;
  onBack: () => void;
  onSignOut: () => void;
}) {
  const { classes } = useClasses();
  /**
   * The account as this screen currently believes it to be.
   *
   * Seeded from the session and replaced by whatever the server returns from a save, so the card above
   * the form updates without a reload — and shows the SERVER's version rather than what was typed,
   * which is the only version that is true.
   */
  const [me, setMe] = useState(user);
  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState(user.firstName ?? "");
  const [lastName, setLastName] = useState(user.lastName ?? "");
  const [nationalId, setNationalId] = useState("");
  const [badId, setBadId] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const name = [me.firstName, me.lastName].filter(Boolean).join(" ");
  const students = classes.reduce((sum, c) => sum + c.students, 0);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    // Checked on submit rather than per keystroke: a check digit is wrong for the first nine digits
    // of every valid id, so validating as they type shows an error the whole way through.
    if (!isValidNationalId(nationalId)) {
      setBadId(true);
      return;
    }
    setBadId(false);
    setBusy(true);
    setError(null);
    try {
      setMe(
        await updateMe({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          nationalId: normaliseNationalId(nationalId),
        }),
      );
      setEditing(false);
      setSaved(true);
    } catch (err) {
      const failure = err instanceof AuthError ? err.failure : "unknown";
      setError(
        failure === "national-id-taken" ? COPY.apply.nationalIdTaken : COPY.enter.failed,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto min-h-full max-w-[560px] px-lg pt-lg pb-xxl">
      <ScreenHeader
        title={COPY.teacher.profile.title}
        onBack={onBack}
        backLabel={COPY.teacher.profile.back}
      />

      {/* The identity card: a disc with the initial, the name, and where they teach. */}
      <div className={`${CARD} mb-lg flex items-center gap-md px-md py-md`}>
        <span
          className="grid size-14 shrink-0 place-items-center rounded-pill bg-grape/14 text-display"
          aria-hidden
        >
          {[...name][0] ?? ""}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-title">{name}</span>
          {me.schoolName && (
            <span className="block truncate text-small text-muted">{me.schoolName}</span>
          )}
        </span>
        {!editing && (
          <button
            onClick={() => {
              setEditing(true);
              setSaved(false);
              setFirstName(me.firstName ?? "");
              setLastName(me.lastName ?? "");
              // Blank rather than pre-filled: the stored id is not sent to the client, and showing a
              // masked value people then edit around is how half a number gets submitted.
              setNationalId("");
              setError(null);
            }}
            className="shrink-0 rounded-pill border border-edge px-md py-1 text-caption text-muted"
          >
            {COPY.teacher.profile.edit}
          </button>
        )}
      </div>

      {saved && <p className="mb-lg text-center text-small text-correct">{COPY.teacher.profile.saved}</p>}

      {editing && (
        <form className={`${CARD} mb-lg flex flex-col gap-md px-md py-md`} onSubmit={submit}>
          <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
            <div>
              <label className={LABEL} htmlFor="first-name">
                {COPY.apply.firstName}
              </label>
              <input
                id="first-name"
                className={FIELD}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
              />
            </div>
            <div>
              <label className={LABEL} htmlFor="last-name">
                {COPY.apply.lastName}
              </label>
              <input
                id="last-name"
                className={FIELD}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
              />
            </div>
          </div>

          <div>
            <label className={LABEL} htmlFor="national-id">
              {COPY.apply.nationalId}
            </label>
            <input
              id="national-id"
              className={`${FIELD} text-center tracking-[2px] ${badId ? "border-wrong" : ""}`}
              value={nationalId}
              // Persian digits folded as they are typed, like every other number field here.
              onChange={(e) => {
                setNationalId(digitsOnly(e.target.value).slice(0, 10));
                setBadId(false);
              }}
              inputMode="numeric"
              dir="ltr"
            />
            <p className={`mt-1 text-caption ${badId ? "text-wrong" : "text-muted"}`}>
              {badId ? COPY.apply.badNationalId : COPY.apply.nationalIdNote}
            </p>
          </div>

          {error && <p className="text-small text-wrong">{error}</p>}

          <button
            type="submit"
            disabled={busy || !firstName.trim() || !lastName.trim() || nationalId.length !== 10}
            className={PRIMARY}
          >
            {busy ? COPY.enter.working : COPY.teacher.profile.save}
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="py-sm text-center text-small text-muted"
          >
            {COPY.teacher.profile.cancel}
          </button>
        </form>
      )}

      <dl className={`${CARD} mb-lg overflow-hidden`}>
        <Row label={COPY.teacher.profile.classes} value={fa(classes.length)} />
        <Row
          label={COPY.teacher.profile.students}
          value={fill(COPY.teacher.classes.count, { count: fa(students) })}
        />
        {/* Shown and not editable, each with its reason. Silence would read as an oversight. */}
        <Row label={COPY.teacher.profile.phoneLabel} note={COPY.teacher.profile.phoneNote} />
        {me.schoolName && (
          <Row
            label={COPY.teacher.profile.schoolLabel}
            value={me.schoolName}
            note={COPY.teacher.profile.schoolNote}
          />
        )}
      </dl>

      {/*
        * Signing out, separated from everything above it.
        *
        * Not styled as a danger: it destroys nothing, and colouring it red would put a warning on the
        * one action a teacher takes on a shared staffroom computer every day. The note beneath is what
        * matters — a child signed in on this device stays signed in, because their session is theirs.
        */}
      <button
        onClick={onSignOut}
        className="min-h-touch w-full rounded-pill border-2 border-edge bg-card px-lg py-md text-heading text-ink"
      >
        {COPY.teacher.signOut}
      </button>
      <p className="mt-sm text-center text-caption text-muted">{COPY.teacher.profile.signOutNote}</p>
    </div>
  );
}

function Row({ label, value, note }: { label: string; value?: string; note?: string }) {
  return (
    <div className="border-b border-edge px-md py-sm last:border-b-0">
      <div className="flex items-center justify-between gap-sm">
        <dt className="text-small text-muted">{label}</dt>
        {value && <dd className="truncate text-body">{value}</dd>}
      </div>
      {note && <p className="mt-1 text-caption text-muted">{note}</p>}
    </div>
  );
}
